/*
 * wim-overlay-popup.js
 *
 * Defines <wim-popup-toskanimed> custom element extending the existing
 * <popup-component> from the Broadcast theme base.
 *
 * Adds:
 *   - Configurable cookie lifetime (data-cookie-days, data-cookie-days-dismissed)
 *   - CTA-driven cookie write (decision = accepted_med | declined | dismissed)
 *   - Dual cookie: writes view_brand alongside popup_toskanimed_decision
 *   - Block click-outside to close (brand-gate must not dismiss without read)
 *   - Focus on heading instead of first focusable (avoid CTA bias)
 *   - dataLayer push opt-in for analytics
 *   - Theme editor force-open bypass
 */

(function () {
  'use strict';

  function init() {
    const Parent = window.customElements.get('popup-component');
    if (!Parent) {
      // Parent web component not yet defined; retry on next tick.
      window.requestAnimationFrame(init);
      return;
    }

    if (window.customElements.get('wim-popup-toskanimed')) {
      return;
    }

    const WimPopupToskanimed = class extends Parent {
      constructor() {
        super();

        this.cookieName = this.popup.getAttribute('data-cookie-name') || 'popup_toskanimed_decision';
        this.cookieDaysDecision = parseInt(this.popup.getAttribute('data-cookie-days') || '30', 10);
        this.cookieDaysDismissed = parseInt(this.popup.getAttribute('data-cookie-days-dismissed') || '1', 10);
        this.dataLayerEnabled = this.getAttribute('data-datalayer-push') === 'true';
        this.headingId = this.popup.getAttribute('aria-labelledby') || null;

        this.bindCtaListeners();
        this.blockBackdropClose();
      }

      // Override: brand-gate must not close on backdrop click.
      // Parent registers a click handler on the dialog that closes on target===DIALOG.
      // We intercept in the capture phase and stopImmediatePropagation when needed.
      blockBackdropClose() {
        this.popup.addEventListener(
          'click',
          function (event) {
            if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
              event.stopImmediatePropagation();
            }
          },
          true // capture phase to win over the parent handler
        );
      }

      // CTAs: write decision cookie (and view_brand when applicable),
      // emit dataLayer event, then let the native <a> navigation happen.
      bindCtaListeners() {
        const self = this;
        const ctaButtons = this.popup.querySelectorAll('[data-popup-cta]');

        ctaButtons.forEach(function (btn) {
          btn.addEventListener('click', function (event) {
            const decision = btn.getAttribute('data-popup-decision');

            if (decision && decision !== 'none') {
              const days = decision === 'dismissed' ? self.cookieDaysDismissed : self.cookieDaysDecision;
              self.writeCookie(self.cookieName, decision, days);

              if (decision === 'accepted_med') {
                self.writeCookie('view_brand', 'med', 7);
              } else if (decision === 'declined') {
                self.writeCookie('view_brand', 'main', 7);
              }

              if (self.dataLayerEnabled && window.dataLayer && typeof window.dataLayer.push === 'function') {
                window.dataLayer.push({
                  event: 'popup_brand_gate_decision',
                  decision: decision,
                });
              }
            }

            // For close button (no href): prevent default and explicitly close the dialog.
            if (!btn.getAttribute('href')) {
              event.preventDefault();
              self.popupClose();
            }
            // For CTAs with href: native navigation proceeds; cookies are already written
            // synchronously above (document.cookie write is sync).
          });
        });
      }

      writeCookie(name, value, days) {
        const maxAge = days * 86400;
        const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
        document.cookie =
          name +
          '=' +
          encodeURIComponent(value) +
          '; expires=' + expires +
          '; max-age=' + maxAge +
          '; path=/; SameSite=Lax; Secure';
      }

      // Override: bypass cookie + triggers when force-open in theme editor
      checkCookie() {
        const isDesignMode = window.Shopify && window.Shopify.designMode === true;
        const forceOpen = this.popup.hasAttribute('data-force-open-editor');

        if (isDesignMode && forceOpen) {
          this.showPopupEvents();
          // Open immediately so the merchant can iterate visually
          window.requestAnimationFrame(this.popupOpen.bind(this));
          return;
        }

        // Brand-gate gating: only `accepted_med` is a terminal decision.
        // Any other value (`declined`, `dismissed`, unknown) — and the absence
        // of the cookie — must re-trigger the popup, so the visitor keeps
        // being asked until they explicitly self-identify as a professional.
        const decision = this.cookie ? this.cookie.read() : false;
        if (decision === 'accepted_med') return;

        this.showPopupEvents();
      }

      // Override: focus the heading (semantic landmark) instead of the first
      // focusable element, which would be the close button or primary CTA and
      // bias the user's decision.
      popupOpen() {
        super.popupOpen();

        const self = this;
        const headingId = this.headingId;
        if (!headingId) return;

        // Defer to next frame so the dialog has been promoted to the top layer
        // before we try to move focus into it.
        window.requestAnimationFrame(function () {
          const heading = self.popup.querySelector('#' + CSS.escape(headingId));
          if (heading) {
            heading.focus({ preventScroll: true });
          }
        });
      }

      // Override: ensure focus is restored to a sensible landmark when the
      // popup was opened by an automatic trigger (no `lastElement` to return to).
      popupCloseActions() {
        super.popupCloseActions();

        const hasLast = window.theme && window.theme.a11y && window.theme.a11y.lastElement;
        if (hasLast) return;

        const fallback = document.getElementById('MainContent') || document.querySelector('main');
        if (fallback) {
          fallback.setAttribute('tabindex', '-1');
          fallback.focus({ preventScroll: true });
        }
      }
    };

    window.customElements.define('wim-popup-toskanimed', WimPopupToskanimed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
